import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a dialog with support for drawer and popup modes.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  // NEW: Store trigger element and animation frame ID
  #triggerElement = null;
  #positionUpdateFrame = null;

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }

    // NEW: Initialize dialog mode based on attribute
    this.#initializeDialogMode();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }

    // NEW: Clean up position tracking
    this.#stopPositionTracking();
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  /**
   * NEW: Initialize dialog mode based on data-dialog-mode attribute
   */
  #initializeDialogMode() {
    const { dialog } = this.refs;
    const mode = this.dialogMode;

    if (mode === 'popup') {
      dialog.classList.add('dialog-popup-mode');
      dialog.classList.remove('dialog-drawer-mode');
    } else {
      dialog.classList.add('dialog-drawer-mode');
      dialog.classList.remove('dialog-popup-mode');
    }
  }

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;

    // NEW: Find and store trigger element
    this.#triggerElement = this.querySelector('[data-dialog-trigger]') || document.querySelector(`[on\\:click*="${this.id}"]`);

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      const mode = this.dialogMode;

      // Only lock scroll for drawer mode, not popup mode
      if (mode === 'drawer') {
        document.body.style.width = '100%';
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;
      }

      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);

      // NEW: Position popup mode relative to trigger button
      if (mode === 'popup') {
        document.body.style.overflow = `hidden`;
        this.#positionPopup();
        
        // NEW: Start continuous position tracking
        this.#startPositionTracking();
      }
    });
  }

  /**
   * NEW: Start tracking popup position relative to trigger
   */
  #startPositionTracking() {
    const updatePosition = () => {
      if (this.refs.dialog.open && this.dialogMode === 'popup') {
        this.#positionPopup();
        this.#positionUpdateFrame = requestAnimationFrame(updatePosition);
      }
    };

    this.#positionUpdateFrame = requestAnimationFrame(updatePosition);
  }

  /**
   * NEW: Stop tracking popup position
   */
  #stopPositionTracking() {
    if (this.#positionUpdateFrame) {
      cancelAnimationFrame(this.#positionUpdateFrame);
      this.#positionUpdateFrame = null;
    }
  }

  /**
   * NEW: Position popup dialog relative to the trigger element
   */
  #positionPopup() {
    const { dialog } = this.refs;
    const trigger = this.#triggerElement;

    if (!trigger) return;

    const triggerRect = trigger.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Reset positioning
    dialog.style.top = '';
    dialog.style.bottom = '';

    // Get dialog dimensions
    const dialogRect = dialog.getBoundingClientRect();

    // Calculate position below the trigger
    let top = triggerRect.bottom + 51; // 8px gap

    // Check if dialog would overflow viewport height
    if (top + dialogRect.height > viewportHeight) {
      top = triggerRect.top - dialogRect.height - 8; // Show above trigger
    }

    // Apply position
    dialog.style.top = `${Math.max(16, top)}px`;
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    // NEW: Stop position tracking
    this.#stopPositionTracking();

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    dialog.classList.add('dialog-closing');

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    const mode = this.dialogMode;

    // Only restore scroll for drawer mode
    if (mode === 'drawer') {
      document.body.style.width = '';
      document.body.style.position = '';
      document.body.style.top = '';
      window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
    }

    document.body.style.overflow = `unset`;
    dialog.close();
    dialog.classList.remove('dialog-closing');

    // NEW: Clear trigger reference
    this.#triggerElement = null;

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }

  /**
   * NEW: Gets the dialog mode (drawer or popup)
   *
   * @returns {string} The dialog mode - 'drawer' or 'popup'
   */
  get dialogMode() {
    return this.getAttribute('data-dialog-mode') || 'drawer';
  }

  /**
   * NEW: Sets the dialog mode
   *
   * @param {string} mode - The dialog mode - 'drawer' or 'popup'
   */
  set dialogMode(mode) {
    this.setAttribute('data-dialog-mode', mode);
    this.#initializeDialogMode();
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;

        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
